import {
  evaluateTemplateValue,
  evaluateTemplateValueCollection,
  dataLocationToPath,
} from "@ea-lab/reactive-json";

/**
 * Error-aware, concurrency-safe Elasticsearch fetch reaction.
 *
 * Why a custom reaction (config-first escape hatch) : `submitData` CAN write the
 * store at an arbitrary path (`updateOnlyData:true` + `updateDataAtLocation`,
 * cf. dist/component/reaction/utility/httpRequestCommon.js) — that is NOT the
 * gap. Its two real shortcomings for a faceted list are :
 *   1. HARD-ERROR surfacing. On a 502 / timeout / network failure its promise
 *      rejects, it skips the store write, and whether the `on: response`
 *      follow-up fires depends on the triggering DOM node still being mounted —
 *      so the `LoadingOverlay` can stay frozen with no message. A faceted list
 *      MUST always recover : close the overlay AND show an error. That guarantee
 *      can't be expressed in YAML, so it lives here.
 *   2. CONCURRENCY. On rapid facet clicks several POSTs overlap and — with a
 *      plain fetch — the LAST RESOLVED wins, not the last emitted → stale list.
 *      We mirror the built-in's `requestKey` + AbortController lock (the Map `f`
 *      with `f.get(i).abort()` in httpRequestCommon.js) : each new request keyed
 *      on `resultPath`/`url` aborts the previous in-flight one, so only the
 *      latest emitted request can write the store.
 *
 * Error banners do NOT stack : `errorPath` is cleared on every start and on
 * every 2xx, so the hard-error banner (`esError`) is empty whenever the backend
 * returns 2xx. Non-blocking backend `messages[]` are a SEPARATE concern rendered
 * from the envelope — distinct from a hard transport error, by design.
 *
 * It owns the full lifecycle in one declarative call :
 *   1. sets `loadingPath` = true,
 *   2. aborts any in-flight request sharing the same key,
 *   3. POSTs `{ filter, search, sort, pagination, sortableColumns }`,
 *   4. on 2xx → writes the unified envelope at `resultPath`, clears the error,
 *   5. on non-2xx / network error → writes a human message at `errorPath`
 *      (previous rows stay visible behind the banner),
 *   6. ALWAYS sets `loadingPath` = false — EXCEPT for a request that was
 *      superseded (aborted), which silently yields to its successor.
 *
 * YAML usage (replaces the loadingOn / submitData / loadingOff trio) :
 *   - what: esFetch
 *     on: click
 *     url: ~~.esQueryUrl
 *     loadingPath: ~~.esLoading
 *     errorPath: ~~.esError
 *     resultPath: ~~.esResult
 *     body:
 *       filter: ~~.selectedFilters
 *       search: ~~.search
 *       sort: ~~.sort
 *       pagination: ~~.pagination
 *       sortableColumns: ~~.sortableColumns
 *
 * Le corps est évalué EN PROFONDEUR : un chemin imbriqué (`es_query: {gid: ~~.id}`,
 * qui porte les jetons [query:…] de la requête Elasticsearch) est résolu comme un
 * chemin de premier niveau — reactive-json n'interpolant pas dans une URL, c'est
 * par là que voyage un identifiant de contexte.
 *
 * Registered in [App.jsx](../../../App.jsx) under `customPlugins.reaction.esFetch`.
 */

// Module-level lock, mirroring httpRequestCommon.js's `f` Map : one in-flight
// AbortController per request key. Starting a new request on a key aborts the
// previous one, so only the latest emitted request resolves into the store.
const inFlightByKey = new Map();

export const esFetchReaction = (props) => {
  const { args, globalDataContext, templateContext } = props;
  if (!args) return;

  const gdc = globalDataContext.getRootContext
    ? globalDataContext.getRootContext()
    : globalDataContext;

  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext: gdc, templateContext });

  const url = resolve(args.url);
  if (!url) return;

  // Resolve store locations to concrete paths once, up front.
  const toPath = (loc) =>
    typeof loc === "string"
      ? dataLocationToPath({
          dataLocation: loc,
          currentPath: templateContext?.templatePath,
          globalDataContext: gdc,
          templateContext,
        })
      : null;

  const loadingPath = toPath(args.loadingPath);
  const errorPath = toPath(args.errorPath);
  const resultPath = toPath(args.resultPath);

  const setAt = (path, value) => {
    if (path) gdc.updateData(value, path);
  };

  // Body templates → concrete values (filter/search/sort/pagination/…).
  const body = args.body
    ? evaluateTemplateValueCollection({
        valueToEvaluate: args.body,
        globalDataContext: gdc,
        templateContext,
        // Deux niveaux : le corps est une STRUCTURE (`es_query: {gid: ~~.id}`,
        // `filter: ~~.selectedFilters`) et un chemin y vit sous une clé. Sans
        // cela (défaut : 1), un `~~.id` imbriqué partirait tel quel au serveur,
        // qui filtrerait sur la chaîne littérale — 0 résultat, sans erreur.
        // Borné à 2 : au-delà on ré-évaluerait les DONNÉES déjà résolues, et une
        // valeur de filtre commençant par `~.` serait relue comme un chemin.
        evaluationDepth: 2,
      })
    : {};

  setAt(loadingPath, true);
  setAt(errorPath, "");

  const headers = {
    "Content-Type": "application/json",
    ...(gdc.headersForRjBuild || {}),
  };

  // Concurrency lock (mirrors httpRequestCommon.js) : a stable key derived from
  // an explicit `requestKey`, else the result path, else the URL. Starting a new
  // request on that key aborts the previous in-flight one so its (stale) result
  // can never win the race.
  const requestKey =
    (typeof args.requestKey === "string" && args.requestKey) ||
    (typeof args.resultPath === "string" && args.resultPath) ||
    url;
  const previous = inFlightByKey.get(requestKey);
  if (previous) previous.abort();
  const controller = new AbortController();
  inFlightByKey.set(requestKey, controller);

  fetch(url, {
    method: "POST",
    // The front is served cross-origin (front-dev.orbit.ddev.site → Drupal API on
    // drupal.orbit.ddev.site). `same-origin` would drop the session cookie on the
    // cross-origin POST → anonymous request → 403 → the list never refreshes.
    // `include` is the project-wide convention for every Drupal API call.
    credentials: "include",
    headers,
    body: JSON.stringify(body),
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok) {
        let detail = "";
        try {
          const payload = await res.json();
          detail = payload && (payload.message || payload.error) ? `: ${payload.message || payload.error}` : "";
        } catch {
          /* non-JSON error body — ignore */
        }
        throw new Error(`HTTP ${res.status}${detail}`);
      }
      return res.json();
    })
    .then((envelope) => {
      setAt(resultPath, envelope);
      setAt(errorPath, "");
    })
    .catch((err) => {
      // Superseded by a newer request : stay silent and let the successor own
      // loading/result/error (it must NOT clear the overlay it didn't open).
      if (err && err.name === "AbortError") return;
      console.error("esFetch:", err);
      // Surface a human message ; the previous results stay visible behind the
      // banner (less jarring than blanking the table), and the overlay closes.
      // `errorMessage` is overridable per call (default below), keeping the
      // reaction reusable on any faceted page.
      const message =
        resolve(args.errorMessage) ||
        "Le chargement a échoué. Réessayez ; si le problème persiste, contactez un administrateur.";
      setAt(errorPath, message);
    })
    .finally(() => {
      // Only the request that still owns the key may release the lock and turn
      // the overlay off ; an aborted/superseded one yields to its successor.
      if (inFlightByKey.get(requestKey) !== controller) return;
      inFlightByKey.delete(requestKey);
      setAt(loadingPath, false);
    });
};
